import lodash from 'lodash';

/**
 * Helpers for the language models of the material editor.
 *
 * A model holds the chapter list of one language: chapters and sections, a section holds
 * chapters. The structure is language neutral, the same chapter carries the same hash in every
 * language.
 */

const HASH_LENGTH = 64;
const HASH_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

/**
 * All chapters of a model in the order of the editor, chapters of sections included.
 */
export const collectModelChapters = (model) => {
    if (!model || !model.list) {
        return [];
    }

    const chapters = [];
    model.list.forEach(item => {
        if (item.type === 'chapter') {
            chapters.push(item);
        }
        else if (item.type === 'section') {
            (item.items || []).forEach(chapter => chapters.push(chapter));
        }
    });

    return chapters;
};

export const collectChaptersByHash = (model) => {
    const chaptersByHash = {};
    collectModelChapters(model).forEach(chapter => {
        chaptersByHash[chapter.hash] = chapter;
    });

    return chaptersByHash;
};

/**
 * The backend always returns the keys as strings, the editor may hold them as numbers.
 */
export const isSameKey = (firstKey, secondKey) => {
    if (firstKey === undefined || firstKey === null || secondKey === undefined || secondKey === null) {
        return false;
    }

    return String(firstKey) === String(secondKey);
};

/**
 * Finds the chapter of a model by its hash, the key the backend and the editor share for a
 * chapter across all languages.
 */
export const findChapterInModel = (model, chapterHash) => {
    if (chapterHash === undefined || chapterHash === null) {
        return null;
    }

    const chapter = lodash.find(collectModelChapters(model), item => isSameKey(item.hash, chapterHash));

    return chapter || null;
};

export const findSectionByUniqueKey = (model, uniqueKey) => {
    return lodash.find(model.list, item => item.type === 'section' && item.uniqueKey === uniqueKey);
};

/**
 * Finds the section a translated section belongs to, by its unique key or else by its id.
 */
export const findSectionForTranslation = (model, translatedSection) => {
    return lodash.find(model.list, item => {
        if (item.type !== 'section') {
            return false;
        }
        if (translatedSection.uniqueKey) {
            return isSameKey(item.uniqueKey, translatedSection.uniqueKey);
        }

        return !!translatedSection.id && isSameKey(item.id, translatedSection.id);
    });
};

/**
 * Chapters are language neutral and identified by their hash, the uniqueKey is only a fallback
 * for chapters that have not been given a hash yet.
 */
export const isSameChapter = (chapter, chapterToCompare) => {
    if (chapter.hash && chapterToCompare.hash) {
        return chapter.hash === chapterToCompare.hash;
    }

    return chapter.uniqueKey === chapterToCompare.uniqueKey;
};

export const createChapterHash = () => {
    let hash = '';
    for (let counter = 0; counter < HASH_LENGTH; counter++) {
        hash += HASH_CHARACTERS.charAt(Math.floor(Math.random() * HASH_CHARACTERS.length));
    }

    return hash;
};

export const createUniqueKey = () => new Date().getTime() + Math.random();

/**
 * The source language chapters in the format the translation endpoint expects.
 */
export const buildSourceChapters = (model) => {
    if (!model) {
        return null;
    }

    return collectModelChapters(model).map(chapter => ({
        uniqueKey: chapter.hash,
        name: chapter.name ? chapter.name : '',
        content: chapter.content ? chapter.content : ''
    }));
};

/**
 * The source language sections in the format the translation endpoint expects.
 */
export const buildSourceSections = (model) => {
    if (!model) {
        return null;
    }

    return model.list
        .filter(item => item.type === 'section')
        .map(section => ({
            id: section.id ? section.id : null,
            uniqueKey: String(section.uniqueKey),
            name: section.name ? section.name : ''
        }));
};



// WEBPACK FOOTER //
// ./src/app/components/modals/courses/authoringTool/editor/materialModels.js